/**
 * DualCtaPresentationExtras
 *
 * Capa de presentación opcional por variante (migración 064) para el funnel intl
 * (DualCTAVideoLanding). Renderiza señales de confianza + testimonios + íconos de
 * pago cuando la variante resuelta por geo trae `presentation`, diferenciando
 * visualmente cada mercado SIN forkear el template.
 *
 * Contrato: `presentation == null` (o sin secciones activadas) → no renderiza
 * nada → el funnel queda byte a byte como antes (cero regresión). El accent del
 * tema se aplica como CSS var local al bloque, no toca el resto del layout.
 *
 * Es deliberadamente compacto: vive en el footerSlot del CTA panel, sobre los
 * links legales, en el estético fullscreen oscuro.
 */

import type { DualVariantPresentation } from "@/lib/serverTemplateDetection";

interface DualCtaPresentationExtrasProps {
  presentation?: DualVariantPresentation | null;
}

/** Sanea un color CSS simple (#hex o nombre corto) para usar como CSS var. */
function safeAccent(v: string | undefined): string | null {
  if (!v || typeof v !== "string") return null;
  const t = v.trim();
  if (/^#[0-9a-fA-F]{3,8}$/.test(t)) return t;
  if (/^[a-zA-Z]{3,20}$/.test(t)) return t; // nombre de color (red, gold, …)
  return null;
}

export default function DualCtaPresentationExtras({
  presentation,
}: DualCtaPresentationExtrasProps) {
  if (!presentation || typeof presentation !== "object") return null;

  const sections = presentation.sections ?? {};
  const accent = safeAccent(presentation.theme?.accent);

  const trustSignals = Array.isArray(presentation.trustSignals)
    ? presentation.trustSignals.filter((s) => typeof s === "string" && s.trim()).slice(0, 6)
    : [];
  const testimonials = Array.isArray(presentation.testimonials)
    ? presentation.testimonials
        .filter((t) => t && typeof t.quote === "string" && t.quote.trim())
        .slice(0, 3)
    : [];
  const paymentMethods = Array.isArray(presentation.paymentMethods)
    ? presentation.paymentMethods.filter((m) => typeof m === "string" && m.trim()).slice(0, 6)
    : [];

  const showTrust = sections.trustBadges !== false && trustSignals.length > 0;
  const showTestimonials = sections.testimonials !== false && testimonials.length > 0;
  const showPayments = sections.paymentIcons === true && paymentMethods.length > 0;

  // Nada que mostrar → no rompemos el layout actual.
  if (!showTrust && !showTestimonials && !showPayments) return null;

  const accentStyle = accent
    ? ({ ["--dual-accent" as string]: accent } as React.CSSProperties)
    : undefined;
  const accentText = accent ? "text-[color:var(--dual-accent)]" : "text-amber-300/80";
  const accentRing = accent
    ? "ring-[color:var(--dual-accent)]/40"
    : "ring-white/15";

  return (
    <div
      style={accentStyle}
      className="mx-auto mt-2 flex max-w-[min(100%,22rem)] flex-col items-center gap-2 text-center"
      data-testid="dual-cta-presentation-extras"
    >
      {showTrust && (
        <div className="flex flex-wrap items-center justify-center gap-1.5">
          {trustSignals.map((sig, i) => (
            <span
              key={i}
              className={`rounded-full bg-white/[0.06] px-2.5 py-1 text-[10px] font-medium tracking-wide text-white/75 ring-1 ${accentRing}`}
            >
              {sig}
            </span>
          ))}
        </div>
      )}

      {showTestimonials && (
        <div className="flex flex-col gap-1.5">
          {testimonials.map((tst, i) => (
            <blockquote key={i} className="text-[11px] leading-snug text-white/70">
              <span className={accentText}>“</span>
              {tst.quote}
              <span className={accentText}>”</span>
              {tst.author ? (
                <cite className="ml-1 not-italic text-white/40">— {tst.author}</cite>
              ) : null}
            </blockquote>
          ))}
        </div>
      )}

      {showPayments && (
        <div className="flex flex-wrap items-center justify-center gap-1 text-[9px] uppercase tracking-wider text-white/35">
          {paymentMethods.map((m, i) => (
            <span key={i} className="rounded bg-white/[0.05] px-1.5 py-0.5">
              {m}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
